import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { Tour } from 'app/types';

type SearchProps = {
  initData: Tour[];
  setData: Dispatch<SetStateAction<Tour[]>>;
  onQueryChange?: (query: string) => void;
  /** Drop the default 366px max-width cap so the bar fills its container.
   *  Used on the dashboard now that the folder controls live below the
   *  folder list and there's extra horizontal room up top. */
  wide?: boolean;
};

const filterTours = (tours: Tour[], query: string): Tour[] => {
  const q = query.trim().toLowerCase();
  if (!q) return tours;
  return tours.filter((tour) => {
    const name = tour.name?.toLowerCase() ?? '';
    const address = tour.address?.toLowerCase() ?? '';
    return name.includes(q) || address.includes(q);
  });
};

function Search({
  initData,
  setData,
  onQueryChange,
  wide,
}: SearchProps): ReactElement {
  const intl = useIntl();
  const [query, setQuery] = useState('');

  const placeholder = intl.formatMessage({
    id: 'personalDashboard.searchInput.placeholder',
    defaultMessage: 'Search your tours',
  });

  // Re-apply the current query whenever the source data updates (e.g.
  // tours finish loading, or the user adds/removes a tour).
  useEffect(() => {
    setData(filterTours(initData, query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData]);

  const handleChange = (value: string) => {
    setQuery(value);
    onQueryChange?.(value);
    setData(filterTours(initData, value));
  };

  return (
    <div
      className={`glass-bar relative flex items-center gap-2 w-full px-4 h-10 ${
        wide ? '' : 'max-w-[366px]'
      }`}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-black/50 shrink-0"
      >
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L14 14" />
      </svg>
      <input
        type="text"
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[13px] text-black/85 placeholder:text-black/40"
      />
      {query && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => handleChange('')}
          className="text-black/40 hover:text-black/75 transition-colors bg-transparent border-0 shrink-0 cursor-pointer p-0 flex items-center justify-center focus:outline-none"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4L12 12M12 4L4 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default Search;
